import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Settings, Save, RefreshCw, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

const OpenAIConfig = () => {
    const [config, setConfig] = useState({
        apiKey: '',
        model: '',
        imageModel: '',
        url: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const fetchConfig = async () => {
        setLoading(true);
        setStatus({ type: '', message: '' });
        try {
            const docRef = doc(db, 'openai', 'openai');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setConfig({
                    apiKey: data['openai:apiKey'] || '',
                    model: data['openai:model'] || '',
                    imageModel: data['openai:imageModel'] || '',
                    url: data['openai:url'] || ''
                });
            } else {
                setStatus({ type: 'warning', message: 'Configuration document not found. Creating a new one may be required.' });
            }
        } catch (error) {
            console.error('Error fetching OpenAI config:', error);
            setStatus({ type: 'error', message: 'Failed to load configuration: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus({ type: '', message: '' });

        try {
            const docRef = doc(db, 'openai', 'openai');
            await updateDoc(docRef, {
                'openai:apiKey': config.apiKey,
                'openai:model': config.model,
                'openai:imageModel': config.imageModel,
                'openai:url': config.url
            });
            setStatus({ type: 'success', message: 'Configuration saved successfully!' });
            
            // Clear success message after 3 seconds
            setTimeout(() => setStatus({ type: '', message: '' }), 3000);
        } catch (error) {
            console.error('Error updating OpenAI config:', error);
            setStatus({ type: 'error', message: 'Failed to save configuration: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium animate-pulse">Loading OpenAI Settings...</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3">
                        <Settings className="w-8 h-8 text-orange" />
                        OpenAI Configuration
                    </h1>
                    <p className="mt-2 text-gray-600">Manage your OpenAI API integration settings and model preferences.</p>
                </div>
                <button
                    onClick={fetchConfig}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
                    title="Refresh settings"
                >
                    <RefreshCw className="w-5 h-5" />
                </button>
            </div>

            {status.message && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-fadeIn ${
                    status.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
                    status.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
                    'bg-amber-50 border border-amber-200 text-amber-700'
                }`}>
                    {status.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <span className="font-bold">{status.message}</span>
                </div>
            )}

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-orange to-orange2 px-8 py-6">
                    <h2 className="text-xl font-bold text-white uppercase tracking-wider">API Settings</h2>
                </div>

                <form onSubmit={handleSave} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* API Key Field */}
                        <div className="col-span-1 md:col-span-2 space-y-2">
                            <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                API Key
                                <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full uppercase tracking-tighter">Required</span>
                            </label>
                            <div className="relative group">
                                <input
                                    type={showKey ? "text" : "password"}
                                    name="apiKey"
                                    value={config.apiKey}
                                    onChange={handleChange}
                                    placeholder="sk-..."
                                    className="w-full px-5 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange2 transition-all font-mono text-gray-700 pr-12"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowKey(!showKey)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange transition-colors"
                                >
                                    {showKey ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 italic">Your OpenAI API key. Keep this secret!</p>
                        </div>

                        {/* Model Field */}
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Text Model</label>
                            <input
                                type="text"
                                name="model"
                                value={config.model}
                                onChange={handleChange}
                                placeholder="e.g. gpt-4o-mini"
                                className="w-full px-5 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange2 transition-all text-gray-700"
                            />
                            <p className="text-xs text-gray-400 italic">Primary model used for text analysis.</p>
                        </div>

                        {/* Image Model Field */}
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Image Model</label>
                            <input
                                type="text"
                                name="imageModel"
                                value={config.imageModel}
                                onChange={handleChange}
                                placeholder="e.g. dall-e-2"
                                className="w-full px-5 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange2 transition-all text-gray-700"
                            />
                            <p className="text-xs text-gray-400 italic">Model used for image generation.</p>
                        </div>

                        {/* URL Field */}
                        <div className="col-span-1 md:col-span-2 space-y-2">
                            <label className="text-sm font-bold text-gray-700">Base URL</label>
                            <input
                                type="url"
                                name="url"
                                value={config.url}
                                onChange={handleChange}
                                placeholder="https://api.openai.com/v1"
                                className="w-full px-5 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange2 transition-all text-gray-700"
                            />
                            <p className="text-xs text-gray-400 italic">OpenAI API base URL (standard: https://api.openai.com/v1)</p>
                        </div>
                    </div>

                    <div className="pt-6 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className={`flex items-center gap-2 px-10 py-4 rounded-2xl bg-gradient-to-r from-orange to-orange2 text-white font-bold text-lg shadow-xl hover:shadow-orange/20 transition-all active:scale-95 ${
                                saving ? 'opacity-70 cursor-not-allowed' : 'hover:from-orange2 hover:to-orange'
                            }`}
                        >
                            {saving ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Save Configuration
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OpenAIConfig;
